import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, UploadCloud, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";
import { useAuth } from "@/context/AuthContext";

export default function Apply() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", reel_url: "" });
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [otherText, setOtherText] = useState("");
  const [resume, setResume] = useState(null);
  const [resumeUploading, setResumeUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const resumeRef = useRef(null);

  useEffect(() => {
    if (user && user.role === "admin") navigate("/admin");
    else if (user && user.role === "creative" && user.status === "approved") navigate("/portal");
  }, [user, navigate]);

  useEffect(() => { api.get("/expertise").then(({ data }) => setOptions(data.expertise)).catch(() => {}); }, []);

  const hasOther = selected.includes("Other");
  const toggle = (name) => setSelected((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));

  const inputCls = "w-full bg-[#050505] border border-[#2A2A2A] px-4 py-3 text-white text-sm focus:outline-none focus:border-[#FF4D00] transition-colors placeholder:text-[#555]";

  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Resume must be a PDF"); return; }
    setResumeUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/leads/resume-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResume(data); toast.success("Resume attached");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setResumeUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error("Please add your name and email"); return; }
    if (selected.length === 0) { toast.error("Select at least one Area of Expertise"); return; }
    if (hasOther && !otherText.trim()) { toast.error("Please describe your 'Other' specialty"); return; }
    if (!resume) { toast.error("Please upload your resume (PDF)"); return; }
    setSending(true);
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        expertise: selected,
        expertise_other: hasOther ? otherText.trim() : "",
        reel_url: form.reel_url,
        resume_file_id: resume.id,
      });
      setDone(true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white grid-noise">
      <header className="border-b border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-8 lg:px-12 py-5 flex items-center justify-between gap-4">
          <Link to="/"><img src={LOGO_URL} alt="Splash Creative Hub" className="w-[140px] h-auto object-contain" /></Link>
          <div className="flex items-center gap-3">
            <Link data-testid="nav-status-link" to="/status" className="text-sm text-[#A1A1AA] hover:text-white transition-colors px-3 py-2">Check status</Link>
            <Link data-testid="nav-login-link" to="/login" className="text-sm border border-[#2A2A2A] text-white px-4 py-2 hover:bg-[#111111] transition-colors">Sign in</Link>
          </div>
        </div>
      </header>

      <section className="max-w-[1000px] mx-auto px-6 lg:px-10 py-16">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#FF4D00] mb-5"><Sparkles className="w-4 h-4" /> The Splash Team · Vetted Application</div>
          <h1 className="font-bebas text-6xl sm:text-7xl leading-[0.9] tracking-tight mb-4">APPLY TO JOIN<br /><span className="text-[#FF4D00]">THE SPLASH TEAM</span></h1>
          <p className="text-[#A1A1AA] text-base max-w-2xl leading-relaxed">
            An invite-worthy collective of elite freelance creatives. Tell us who you are and what you do. We review every application by hand — no password needed to apply or track your status.
          </p>
        </motion.div>

        {done ? (
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-12 border border-[#2A2A2A] bg-[#111111] p-10" data-testid="application-success">
            <div className="text-xs uppercase tracking-[0.25em] text-[#10B981] mb-4">Application Received</div>
            <h2 className="font-bebas text-4xl mb-3">YOU'RE IN THE QUEUE</h2>
            <p className="text-sm text-[#A1A1AA] leading-relaxed max-w-xl mb-8">
              Thanks for applying. Your application is now <strong className="text-white">under review</strong>. We've emailed you a confirmation. You can track your progress anytime with a one-time code.
            </p>
            <Link data-testid="go-check-status-btn" to="/status" className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-6 py-3 text-sm hover:bg-[#E64500] transition-colors">
              Check your status <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        ) : (
          <form onSubmit={submit} className="mt-12 border border-[#2A2A2A] bg-[#111111] p-8 space-y-5 max-w-2xl" data-testid="application-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <input data-testid="app-name-input" className={inputCls} placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <input data-testid="app-email-input" type="email" className={inputCls} placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-2">Areas of Expertise</label>
              <div className="flex flex-wrap gap-2" data-testid="app-expertise-options">
                {[...options.map((o) => o.name), "Other"].map((name) => (
                  <button type="button" key={name} data-testid={`app-expertise-${name}`} onClick={() => toggle(name)}
                    className={`px-3 py-2 text-sm border transition-colors ${selected.includes(name) ? "border-[#FF4D00] bg-[#FF4D0020] text-white" : "border-[#2A2A2A] text-[#A1A1AA] hover:border-[#FF4D00]"}`}>{name}</button>
                ))}
              </div>
              {hasOther && (
                <input data-testid="app-expertise-other-input" className={`${inputCls} mt-3`} placeholder="Tell us your specialty…" value={otherText} onChange={(e) => setOtherText(e.target.value)} />
              )}
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Link to your reel</label>
              <input data-testid="app-reel-input" className={inputCls} placeholder="https://vimeo.com/your-reel (optional)" value={form.reel_url} onChange={(e) => setForm({ ...form, reel_url: e.target.value })} />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] block mb-1">Resume (PDF)</label>
              <div onClick={() => !resumeUploading && resumeRef.current?.click()} data-testid="app-resume-upload"
                className={`cursor-pointer border border-dashed p-4 flex items-center gap-3 transition-colors ${resume ? "border-[#10B981] bg-[#10B98110]" : "border-[#2A2A2A] hover:border-[#FF4D00]"}`}>
                <input ref={resumeRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={uploadResume} />
                {resumeUploading ? <Loader2 className="w-5 h-5 animate-spin text-[#FF4D00]" /> : resume ? <FileText className="w-5 h-5 text-[#10B981]" /> : <UploadCloud className="w-5 h-5 text-[#A1A1AA]" />}
                <span className="text-sm text-[#A1A1AA] truncate">{resume ? resume.original_filename : "Attach your resume (PDF)"}</span>
              </div>
            </div>

            <button data-testid="app-submit-btn" disabled={sending || resumeUploading} className="inline-flex items-center gap-2 bg-[#FF4D00] text-white px-7 py-3.5 text-sm hover:bg-[#E64500] transition-colors disabled:opacity-60">
              {sending && <Loader2 className="w-4 h-4 animate-spin" />} Submit application <ArrowRight className="w-4 h-4" />
            </button>
            <p className="text-xs text-[#555]">Already applied? <Link to="/status" className="text-[#FF4D00] hover:underline">Check your status</Link> with a one-time code.</p>
            <p className="text-xs text-[#555] leading-relaxed border-t border-[#2A2A2A] pt-4" data-testid="app-security-note">Your privacy is our priority — all documents are encrypted at rest and in transit, with access strictly limited to authorized administrators for payroll and compliance only.</p>
          </form>
        )}
      </section>

      <footer className="border-t border-[#2A2A2A]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-[#A1A1AA]">
          <span className="font-bebas text-xl text-white tracking-wide">SPLASH CREATIVE HUB</span>
          <div className="flex items-center gap-5">
            <Link data-testid="footer-privacy-link" to="/privacy" className="hover:text-white transition-colors">Privacy &amp; Security</Link>
            <span>© 2026 Splash Creative Hub. All rights reserved.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
